import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Stethoscope, Users, PhoneIncoming, CalendarX2, Layers, Mail,
  ArrowRight, ArrowLeft, Sparkles, CheckCircle2, AlertCircle,
  TrendingDown, DollarSign, Loader2, ShieldCheck, Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDemo } from "@/components/landing/DemoContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============================================================================
   Steps definition — each is a single-question step with chip options
   =========================================================================== */
const STEPS = [
  {
    key: "clinic_type",
    Icon: Stethoscope,
    eyebrow: "Step 1 of 5",
    label: "What kind of clinic do you run?",
    options: ["Physiotherapy", "Chiropractic", "Dental", "Optometry", "Veterinary", "Spa / Wellness", "Mental Health", "Multi-specialty"],
  },
  {
    key: "team_size",
    Icon: Users,
    eyebrow: "Step 2 of 5",
    label: "How big is your team?",
    options: ["solo", "2-5", "6-15", "16+"],
    optionLabel: (v) => v === "solo" ? "Solo" : `${v} clinicians`,
  },
  {
    key: "monthly_calls",
    Icon: PhoneIncoming,
    eyebrow: "Step 3 of 5",
    label: "Roughly how many inbound calls per month?",
    options: ["<100", "100-300", "300-800", "800+"],
    optionLabel: (v) => v,
  },
  {
    key: "no_show_rate",
    Icon: CalendarX2,
    eyebrow: "Step 4 of 5",
    label: "What's your typical no-show / late-cancel rate?",
    options: ["<5%", "5-10%", "10-20%", "20%+"],
  },
  {
    key: "tools_count",
    Icon: Layers,
    eyebrow: "Step 5 of 5",
    label: "How many tools do you log into in a typical week?",
    options: ["1-3", "4-6", "7-9", "10+"],
    optionLabel: (v) => `${v} tools`,
  },
];

/* ============================================================================
   Component
   =========================================================================== */
export const ClinicAudit = () => {
  const openDemo = useDemo();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const step = STEPS[stepIdx];
  const totalSteps = STEPS.length + 1; // +1 for the contact form
  const isContact = stepIdx === STEPS.length;
  const progress = Math.round(((stepIdx + 1) / totalSteps) * 100);

  const selectOption = (value) => {
    setAnswers((a) => ({ ...a, [step.key]: value }));
    setTimeout(() => setStepIdx((i) => i + 1), 220);
  };

  const goBack = () => {
    setStepIdx((i) => Math.max(0, i - 1));
    setError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !clinicName.trim()) {
      setError("Please add your clinic name and email.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/audit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...answers,
          email: email.trim(),
          clinic_name: clinicName.trim(),
        }),
      });
      if (!res.ok) throw new Error("Failed to submit");
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setError("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStepIdx(0);
    setAnswers({});
    setEmail("");
    setClinicName("");
    setResult(null);
    setError("");
  };

  return (
    <section
      id="clinic-audit"
      data-testid="clinic-audit-section"
      className="relative overflow-hidden bg-gradient-to-b from-violet-50/40 via-blue-50/30 to-emerald-50/30 py-20 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0a0a14] px-6 py-14 sm:px-12 sm:py-20 lg:px-20">
          {/* Layered ambient glow */}
          <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-violet-600/30 blur-[140px]" />
          <div className="pointer-events-none absolute -bottom-40 -left-40 h-[480px] w-[480px] rounded-full bg-blue-600/25 blur-[140px]" />
          <div className="pointer-events-none absolute right-1/4 top-1/2 h-[260px] w-[260px] rounded-full bg-emerald-500/15 blur-[120px]" />
          <div className="pointer-events-none absolute inset-0 bg-grid opacity-25" />
          <div className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-white/5" />

          <div className="relative grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
            {/* LEFT — Pitch column */}
            <div className="lg:col-span-5 text-left">
              <motion.span
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-white/80 backdrop-blur"
                data-testid="audit-eyebrow"
              >
                <Sparkles className="h-3 w-3 text-emerald-400" />
                Free · 60-second audit
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="mt-5 font-display text-[2rem] font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-4xl lg:text-5xl"
              >
                See how much your clinic{" "}
                <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-emerald-300 bg-clip-text text-transparent">
                  is leaving on the table.
                </span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-6 text-base leading-relaxed text-white/70 sm:text-lg lg:text-xl"
              >
                Answer 5 quick questions. We&apos;ll show your{" "}
                <span className="font-semibold text-white/90">Clinic OS readiness score</span>
                {" "}and estimate the revenue you&apos;re losing every month — built around your real numbers, not generic averages.
              </motion.p>

              <ul className="mt-7 space-y-2.5 text-sm text-white/80">
                {[
                  "Personalised score · 0–100",
                  "Estimated monthly $ loss from missed calls + no-shows",
                  "3 quick wins matched to your clinic type",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-400" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>No spam · No credit card · Just numbers</span>
              </div>
            </div>

            {/* RIGHT — Interactive audit panel */}
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] shadow-2xl backdrop-blur-xl">
                {/* Progress bar */}
                <div className="border-b border-white/10 px-5 py-3 sm:px-7">
                  <div className="flex items-center justify-between text-[10.5px] font-semibold uppercase tracking-[0.18em] text-white/55">
                    <span>{result ? "Your audit" : isContact ? "One last step" : step.eyebrow}</span>
                    <span className="font-mono text-white/70">{result ? "Complete" : `${progress}%`}</span>
                  </div>
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-violet-500 via-blue-500 to-emerald-500"
                      initial={{ width: "0%" }}
                      animate={{ width: result ? "100%" : `${progress}%` }}
                      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                    />
                  </div>
                </div>

                {/* Body */}
                <div className="px-5 py-8 sm:px-7 sm:py-10">
                  <AnimatePresence mode="wait">
                    {/* QUESTION STEPS */}
                    {!isContact && !result && (
                      <motion.div
                        key={`q-${stepIdx}`}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.35 }}
                        className="text-left"
                      >
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/80 to-blue-500/80 text-white shadow-lg shadow-violet-500/30">
                            <step.Icon className="h-5 w-5" />
                          </span>
                          <h3 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">
                            {step.label}
                          </h3>
                        </div>

                        <div className="mt-6 grid gap-2 sm:grid-cols-2">
                          {step.options.map((opt) => {
                            const selected = answers[step.key] === opt;
                            const label = step.optionLabel ? step.optionLabel(opt) : opt;
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => selectOption(opt)}
                                data-testid={`audit-opt-${step.key}-${opt}`}
                                className={`group inline-flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                                  selected
                                    ? "border-violet-400/70 bg-gradient-to-r from-violet-500/25 to-blue-500/15 text-white shadow-md shadow-violet-500/30"
                                    : "border-white/12 bg-white/[0.04] text-white/80 hover:border-violet-400/45 hover:bg-white/[0.07] hover:text-white"
                                }`}
                              >
                                <span>{label}</span>
                                <ArrowRight className={`h-4 w-4 flex-none transition-transform group-hover:translate-x-1 ${selected ? "text-emerald-300" : "text-white/40"}`} />
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-6 flex items-center justify-between text-[11px] text-white/55">
                          <button
                            type="button"
                            disabled={stepIdx === 0}
                            onClick={goBack}
                            data-testid="audit-back"
                            className={`inline-flex items-center gap-1.5 font-semibold transition-colors ${
                              stepIdx === 0 ? "opacity-30 cursor-not-allowed" : "hover:text-white"
                            }`}
                          >
                            <ArrowLeft className="h-3 w-3" /> Back
                          </button>
                          <span className="font-mono">≈ {Math.max(5, 60 - stepIdx * 10)}s left</span>
                        </div>
                      </motion.div>
                    )}

                    {/* CONTACT STEP */}
                    {isContact && !result && (
                      <motion.form
                        key="contact"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.35 }}
                        onSubmit={handleSubmit}
                        className="text-left"
                        data-testid="audit-contact-form"
                      >
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30">
                            <Mail className="h-5 w-5" />
                          </span>
                          <h3 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">
                            Where should we send your audit?
                          </h3>
                        </div>
                        <p className="mt-3 text-sm text-white/70">
                          We&apos;ll show your score now and email a copy plus the 3 quick-win actions for your clinic.
                        </p>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                          <input
                            type="text"
                            value={clinicName}
                            onChange={(e) => setClinicName(e.target.value)}
                            placeholder="Clinic name"
                            data-testid="audit-input-clinic-name"
                            className="rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-white/40 focus:border-violet-400/60 focus:bg-white/[0.07]"
                          />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@clinic.com"
                            data-testid="audit-input-email"
                            className="rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-white/40 focus:border-violet-400/60 focus:bg-white/[0.07]"
                          />
                        </div>

                        {error && (
                          <p className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-rose-300">
                            <AlertCircle className="h-3.5 w-3.5" /> {error}
                          </p>
                        )}

                        <div className="mt-6 flex items-center gap-3">
                          <button
                            type="button"
                            onClick={goBack}
                            data-testid="audit-back-contact"
                            className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.04] px-4 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.08] hover:text-white"
                          >
                            <ArrowLeft className="h-4 w-4" /> Back
                          </button>
                          <button
                            type="submit"
                            disabled={submitting}
                            data-testid="audit-submit"
                            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-5 text-sm font-bold text-white shadow-lg shadow-violet-600/40 transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
                          >
                            {submitting ? (
                              <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
                            ) : (
                              <>Show me my audit <ArrowRight className="h-4 w-4" /></>
                            )}
                          </button>
                        </div>
                        <p className="mt-3 text-[10.5px] uppercase tracking-[0.18em] text-white/45">
                          We never share your email · Unsubscribe anytime
                        </p>
                      </motion.form>
                    )}

                    {/* RESULT */}
                    {result && (
                      <motion.div
                        key="result"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.4 }}
                        data-testid="audit-result"
                        className="text-left"
                      >
                        <ResultPanel result={result} openDemo={openDemo} reset={reset} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================================
   Result panel
   =========================================================================== */
const ResultPanel = ({ result, openDemo, reset }) => {
  const score = result.score ?? 0;
  const loss = result.monthly_loss_estimate ?? 0;
  const tier = useMemo(() => {
    if (score >= 75) return { label: "Strong",   accent: "text-emerald-300", bg: "from-emerald-500/30 to-emerald-500/0" };
    if (score >= 50) return { label: "Decent",   accent: "text-amber-300",   bg: "from-amber-500/30 to-amber-500/0"  };
    return                       { label: "Risky",   accent: "text-rose-300",    bg: "from-rose-500/30 to-rose-500/0"    };
  }, [score]);

  const wins = useMemo(() => {
    const out = [];
    if (["100-300", "300-800", "800+"].includes(result.monthly_calls)) {
      out.push({ Icon: PhoneIncoming, title: "Turn missed calls into bookings",       sub: "AI receptionist + auto text-back — recover 30-60% of misses." });
    }
    if (["10-20%", "20%+"].includes(result.no_show_rate)) {
      out.push({ Icon: CalendarX2,    title: "Cut no-shows with 24h confirmations",   sub: "Day-before SMS confirms, reschedules or cancels for you." });
    }
    if (["7-9", "10+"].includes(result.tools_count)) {
      out.push({ Icon: Wrench,        title: "Collapse your tool stack to one OS",    sub: "Inbox, calendar, recall, reviews, reporting under one login." });
    }
    while (out.length < 3) {
      out.push({ Icon: Sparkles, title: "Activate Clinic OS workflows", sub: "Reactivation, reviews, and recall on autopilot." });
    }
    return out.slice(0, 3);
  }, [result]);

  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 text-white shadow-lg shadow-violet-500/30">
          <Sparkles className="h-5 w-5" />
        </span>
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-emerald-300">
            Your audit · {result.clinic_name}
          </p>
          <h3 className="font-display text-xl font-black tracking-tight text-white sm:text-2xl">
            Here&apos;s where {result.clinic_name} stands.
          </h3>
        </div>
      </div>

      {/* Score + Loss row */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="relative overflow-hidden rounded-2xl border border-white/12 bg-white/[0.04] p-5">
          <div className={`pointer-events-none absolute -inset-2 -z-0 rounded-3xl bg-gradient-to-br ${tier.bg} blur-3xl`} />
          <div className="relative">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-white/55">
              Clinic OS readiness
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="font-display text-5xl font-black tracking-tight text-white">{score}</span>
              <span className="text-sm text-white/55">/ 100</span>
            </div>
            <p className={`mt-1 text-sm font-bold ${tier.accent}`}>{tier.label}</p>
            {/* score gauge */}
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-rose-500 via-amber-400 to-emerald-500"
                style={{ width: `${score}%` }}
              />
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-rose-400/25 bg-rose-500/5 p-5">
          <div className="pointer-events-none absolute -inset-2 -z-0 rounded-3xl bg-rose-500/20 blur-3xl" />
          <div className="relative">
            <p className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-rose-300">
              <TrendingDown className="h-3 w-3" /> Estimated monthly leakage
            </p>
            <div className="mt-1 flex items-baseline gap-1.5">
              <DollarSign className="h-5 w-5 text-rose-300" />
              <span className="font-display text-5xl font-black tracking-tight text-white">
                {loss.toLocaleString()}
              </span>
            </div>
            <p className="mt-1 text-xs text-white/65">
              From missed calls + no-shows at ~$120 avg ticket
            </p>
          </div>
        </div>
      </div>

      {/* Quick wins */}
      <div className="mt-6">
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-white/55">
          Your 3 quick wins
        </p>
        <div className="mt-2 space-y-2">
          {wins.map((w, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-violet-500/80 to-blue-500/80 text-white">
                <w.Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-black tracking-tight text-white">{w.title}</p>
                <p className="text-[12px] text-white/65">{w.sub}</p>
              </div>
              <span className="hidden text-[10px] font-bold text-emerald-300 sm:inline">+{(i + 1) * 7}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTAs */}
      <div className="mt-7 flex flex-col gap-3 sm:flex-row">
        <Link
          to={`/build?type=${encodeURIComponent(result.clinic_type)}&team=${encodeURIComponent(result.team_size)}&calls=${encodeURIComponent(result.monthly_calls)}&noshow=${encodeURIComponent(result.no_show_rate)}&tools=${encodeURIComponent(result.tools_count)}&from=audit`}
          data-testid="audit-cta-build"
          className="group inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-5 text-sm font-bold text-white shadow-lg shadow-violet-600/40 transition-all hover:-translate-y-0.5 hover:shadow-xl"
        >
          Build Your OS <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
        <button
          type="button"
          onClick={openDemo}
          data-testid="audit-cta-demo"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-5 text-sm font-semibold text-white transition-colors hover:bg-white/[0.08]"
        >
          Book a demo instead
        </button>
        <button
          type="button"
          onClick={reset}
          data-testid="audit-restart"
          className="inline-flex h-12 items-center justify-center px-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/55 hover:text-white"
        >
          Restart
        </button>
      </div>
    </div>
  );
};
